import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BoardCreateInput, BoardPatch, IssuePatch } from '@cortex/domain-org-issues';
import { orgIssuesApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  OrgIssueCreateRequest,
  OrgIssueFilter,
  OrgIssueSharingInput,
} from '../lib/api/org-issues.js';

/**
 * 見られる組織課題ボードの一覧。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が見られる組織課題ボードを、できること（課題を作れる・設定を変えられる）つきで取得する
 * @graph-connects /org-issues/boards [calls] ボード一覧API
 * @graph-connects orgIssuesApi.listBoards [calls] 組織課題APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useOrgBoards() {
  return useQuery({
    queryKey: queryKeys.orgIssues.boards(),
    queryFn: () => orgIssuesApi.listBoards(),
  });
}

/**
 * 自分が担当の未完了 Next（見られるボード横断）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が担当の未完了 Next を、見られるボード横断で期日の近い順に取得する
 * @graph-connects /org-issues/actions [calls] 自分の未完了 Next API
 * @graph-connects orgIssuesApi.myActions [calls] 組織課題APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMyOrgActions() {
  return useQuery({
    queryKey: queryKeys.orgIssues.myActions(),
    queryFn: () => orgIssuesApi.myActions(),
  });
}

/**
 * ボードの課題一覧（ボードの設定と、できることも一緒に返る）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードの課題を絞り込み条件つきで取得し、看板に並べる元にする
 * @graph-connects /org-issues/boards/{boardId}/issues [calls] 課題一覧API
 * @graph-connects orgIssuesApi.listIssues [calls] 組織課題APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useOrgIssues(boardId: string, filter: OrgIssueFilter = {}) {
  return useQuery({
    queryKey: queryKeys.orgIssues.issues(boardId, filter),
    queryFn: () => orgIssuesApi.listIssues(boardId, filter),
  });
}

/**
 * 課題 1 件の詳細（コメント・履歴つき）。開いているときだけ取る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題 1 件の本文・Next・コメント・履歴を取得する
 * @graph-connects /org-issues/boards/{boardId}/issues/{issueId} [calls] 課題詳細API
 * @graph-connects orgIssuesApi.getIssue [calls] 組織課題APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useOrgIssue(boardId: string, issueId: string | null) {
  return useQuery({
    queryKey: queryKeys.orgIssues.issue(boardId, issueId ?? ''),
    queryFn: issueId === null ? skipToken : () => orgIssuesApi.getIssue(boardId, issueId),
  });
}

/**
 * ボードを見られる在籍者（担当・Next の担当の候補）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードを見られる在籍者を、担当を選ぶ候補として取得する
 * @graph-connects /org-issues/boards/{boardId}/members [calls] 担当の候補API
 * @graph-connects orgIssuesApi.boardMembers [calls] 組織課題APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useOrgBoardMembers(boardId: string) {
  return useQuery({
    queryKey: queryKeys.orgIssues.members(boardId),
    queryFn: () => orgIssuesApi.boardMembers(boardId),
  });
}

/**
 * 組織課題の読み取りを全部読み直す。
 *
 * 1 つの書き込みが一覧・詳細・自分の Next・（公開範囲を変えれば）ボード一覧と担当の候補の
 * どれにも効くので、どれに効くかを書き込みごとに数えずに全部無効化する。
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
function useRefreshOrgIssues(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.orgIssues.all });
  };
}

/**
 * ボードを作る（所有組織のリーダーと admin だけ。判定は API）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードを所有組織・公開範囲を指定して作る
 * @graph-connects /org-issues/boards [calls] ボード作成API
 * @graph-connects orgIssuesApi.createBoard [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 作成後の再取得
 */
export function useCreateOrgBoard() {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: (input: BoardCreateInput) => orgIssuesApi.createBoard(input),
    onSuccess: refresh,
  });
}

/**
 * ボードの設定を変える（編集者だけ。判定は API）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードの名前・公開範囲・編集者・列・本文テンプレートを変える
 * @graph-connects /org-issues/boards/{boardId} [calls] ボード設定変更API
 * @graph-connects orgIssuesApi.updateBoard [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 変更後の再取得
 */
export function useUpdateOrgBoard(boardId: string) {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: (patch: BoardPatch) => orgIssuesApi.updateBoard(boardId, patch),
    onSuccess: refresh,
  });
}

/**
 * 課題を作る（起点は画面＝`manual`）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードに課題を起票する
 * @graph-connects /org-issues/boards/{boardId}/issues [calls] 課題作成API
 * @graph-connects orgIssuesApi.createIssue [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 作成後の再取得
 */
export function useCreateOrgIssue(boardId: string) {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: (input: OrgIssueCreateRequest) => orgIssuesApi.createIssue(boardId, input),
    onSuccess: refresh,
  });
}

/**
 * 課題の中身（本文・優先度・担当・ラベル・Next・リンク・決着メモ）を更新する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題の本文・優先度・担当・ラベル・Next・リンク・決着メモを更新する
 * @graph-connects /org-issues/boards/{boardId}/issues/{issueId} [calls] 課題更新API
 * @graph-connects orgIssuesApi.updateIssue [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 更新後の再取得
 */
export function useUpdateOrgIssue(boardId: string) {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: ({ issueId, patch }: { issueId: string; patch: IssuePatch }) =>
      orgIssuesApi.updateIssue(boardId, issueId, patch),
    onSuccess: refresh,
  });
}

/**
 * 課題の列を移す（状態変更。編集者か担当だけ。判定は API）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題を看板の別の列へ移して状態を変える
 * @graph-connects /org-issues/boards/{boardId}/issues/{issueId}/move [calls] 列の移動API
 * @graph-connects orgIssuesApi.moveIssue [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 移動後の再取得
 */
export function useMoveOrgIssue(boardId: string) {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: ({ issueId, columnId }: { issueId: string; columnId: string }) =>
      orgIssuesApi.moveIssue(boardId, issueId, columnId),
    onSuccess: refresh,
  });
}

/**
 * ピン留め・共有状態を変える（編集者か作成者だけ。判定は API）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題のピン留めと共有状態（共有・下書き・非共有）を変える
 * @graph-connects /org-issues/boards/{boardId}/issues/{issueId}/sharing [calls] 共有設定API
 * @graph-connects orgIssuesApi.setSharing [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 変更後の再取得
 */
export function useSetOrgIssueSharing(boardId: string) {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: ({ issueId, input }: { issueId: string; input: OrgIssueSharingInput }) =>
      orgIssuesApi.setSharing(boardId, issueId, input),
    onSuccess: refresh,
  });
}

/**
 * 課題にコメントを足す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題にコメントを足す
 * @graph-connects /org-issues/boards/{boardId}/issues/{issueId}/comments [calls] コメント追加API
 * @graph-connects orgIssuesApi.addComment [calls] 組織課題APIクライアント呼び出し
 * @graph-connects useRefreshOrgIssues [calls] 追加後の再取得
 */
export function useAddOrgIssueComment(boardId: string) {
  const refresh = useRefreshOrgIssues();
  return useMutation({
    mutationFn: ({ issueId, body }: { issueId: string; body: string }) =>
      orgIssuesApi.addComment(boardId, issueId, body),
    onSuccess: refresh,
  });
}
